import React, { useCallback, useMemo } from 'react';
import FilterChip from '../filterChip/FilterChip';
import ContextualMenu, {
    ContextualMenuItem,
} from '../contextualMenu/ContextualMenu';
import { useTranslation } from 'react-i18next';

export interface FilterChipEnumItem {
    label: string;
    value: string | number | boolean;
}

export type FilterChipEnumValue = string | number | null | boolean | undefined; // null is empty; undefined is filter off
export type FilterChipEnumOnChange = (value: FilterChipEnumValue) => void;

export interface Props {
    emptyAllowed?: boolean;
    items: FilterChipEnumItem[];
    label: string;
    onChange: FilterChipEnumOnChange;
    onRemove?: () => void;
    value: FilterChipEnumValue;
}

const FilterChipEnum: React.FC<Props> = (props) => {
    const [
        anchorElement,
        setAnchorElement,
    ] = React.useState<HTMLElement | null>(null);
    const [clicked, setClicked] = React.useState<boolean>(false);
    const { t } = useTranslation();

    const {
        emptyAllowed = false,
        items,
        label,
        onChange,
        onRemove,
        value,
    } = props;

    const translatedAll = t('-- not selected --');
    const translatedEmpty = t('Empty');

    const getLabelByValue = useCallback(
        (valueParam) => {
            if (valueParam === null) {
                return translatedEmpty;
            }
            if (valueParam === undefined) {
                return translatedAll;
            }
            const foundItem = items.find(
                (item: FilterChipEnumItem) => item.value === valueParam
            );
            if (foundItem === undefined) {
                return translatedAll;
            }
            return foundItem.label;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items, value]
    );

    const singleLevelList = useMemo<
        ContextualMenuItem[]
    >((): ContextualMenuItem[] => {
        const output: ContextualMenuItem[] = [];
        output.push({
            text: translatedAll,
            onClick: () => {
                setAnchorElement(null);
                setClicked(false);
                onChange(undefined);
            },
        });
        if (emptyAllowed) {
            output.push({
                text: translatedEmpty,
                onClick: () => {
                    setAnchorElement(null);
                    setClicked(false);
                    onChange(null);
                },
            });
        }
        items.forEach((item: FilterChipEnumItem) => {
            output.push({
                text: item.label,
                id: item.value.toString(),
                onClick: () => {
                    setAnchorElement(null);
                    setClicked(false);
                    onChange(item.value);
                },
            });
        });

        return output;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, clicked, setClicked]);

    const handleExpand = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        setClicked(true);
        setAnchorElement(event.currentTarget);
    };

    const handleCollapse = (): void => {
        setClicked(false);
        setAnchorElement(null);
    };

    return (
        <>
            <FilterChip
                clicked={clicked}
                label={label}
                onCollapse={handleCollapse}
                onExpand={handleExpand}
                onRemove={onRemove}
                values={[getLabelByValue(value)]}
            />
            <ContextualMenu
                anchorElement={anchorElement}
                handleClose={handleCollapse}
                singleLevelList={singleLevelList}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            />
        </>
    );
};

export default FilterChipEnum;
